import {
  Section,
  SectionHeader,
  Paper,
  Link,
  SectionProps,
  NavLink,
} from "@liberetech/design-system";
import classNames from "classnames";

import {
  useFeatureIsOn,
  useFeaturesReady,
} from "components/Context/ExperimentContext";
import { useSearchBar } from "components/Context/PageContext";
import { useSession } from "components/Context/SessionContext";
import { Trans, useTranslation } from "lib/i18n/client";
import { Promotion } from "lib/promotion/promotion";
import { Brand } from "types";

import styles from "./SectionWithPromo.module.css";

const SectionWithPromo: React.FC<SectionWithPromoType & SectionProps> = ({
  title,
  description,
  label,
  brand,
  children,
  promotion,
  ...rest
}) => {
  const session = useSession();
  const searchBar = useSearchBar();
  const { t } = useTranslation("sectionWithPromo");
  const isLeisure =
    !session.session?.accountId || session.session.accountType === "INHABITANT";

  const crossOutPricesExperimentEnabled = useFeatureIsOn(
    "show-direct-channel-discount",
  );
  const isFeaturesReady = useFeaturesReady();

  return (
    <Section className={styles.promoSection} {...rest}>
      <div className={classNames("grid", styles.promoSectionHeader)}>
        <SectionHeader label={label} title={title} description={description} />
        <Paper className={styles.promos} elevation={0}>
          <div className={styles.promo}>
            <strong>{t("flexible.title")}</strong>
            <p>{t("flexible.claim")}</p>
          </div>
          <div className={styles.promo}>
            <strong>{t("stayLonger.title", { brand: brand.name })}</strong>
            <p>
              {t(
                `stayLonger.claim${crossOutPricesExperimentEnabled && isFeaturesReady ? "_crossOutPricesExperiment" : ""}`,
              )}
              {". "}
              <button
                onClick={() => {
                  searchBar.open();
                  window.dataLayer?.push({
                    event: "click",
                    event_category: "section_with_promo",
                    event_label: "promo_stay_longer",
                  });
                }}
              >
                <Link>{t("stayLonger.link")}</Link>
              </button>
            </p>
          </div>
          {promotion && isLeisure && (
            <div className={styles.temporaryPromo}>
              <Trans
                i18nKey={
                  promotion.translateKey
                    ? `commonSearchBar:promotion.${promotion.translateKey}`
                    : `commonSearchBar:promotion.campaign.discount_${promotion.discount.type}${crossOutPricesExperimentEnabled && isFeaturesReady ? "_crossOutPricesExperiment" : ""}`
                }
                components={{ strong: <strong /> }}
                values={{ discount: promotion.discount.amount }}
              />{" "}
              <Trans
                i18nKey={"commonSearchBar:promotion.campaign.promoCode"}
                components={{ strong: <strong /> }}
                values={{ promoCode: promotion.promoCode }}
              />{" "}
              {promotion.loyaltyApplies && (
                <>
                  {" "}
                  <Trans
                    i18nKey="commonSearchBar:promotion.campaign.loyaltyApplies"
                    components={{ linkCommunity: <CommunityLink /> }}
                    values={{ brand: brand.name }}
                  />
                </>
              )}
              <p className={styles.temporaryPromoTerms}>
                *
                {t(`commonSearchBar:promotion.campaign.terms`, {
                  maxNights: promotion.maxNights,
                  startDate: promotion.startDate,
                  endDate: promotion.endDate,
                })}
              </p>
            </div>
          )}
        </Paper>
      </div>
      {children}
    </Section>
  );
};

const CommunityLink: React.FC = ({ children }) => (
  <NavLink href="/community">
    <Link>{children}</Link>
  </NavLink>
);

type SectionWithPromoType = {
  title: string;
  description?: string;
  promotion?: Promotion | null;
  label: string;
  brand: Brand;
};

export default SectionWithPromo;
